import { styled } from '@mui/material/styles';
import { getZoomAdjustedPx } from 'components/ui/utilities/zoom';
import { PRISM_BREAKPOINTS } from 'theme';
import { WORKSPACE_PANE_LAYOUT_DEFAULTS } from './WorkspacePaneLayout.constants';

interface WorkspacePaneLayoutRootStyleProps {
  centerMinWidth: number;
  hasRightPane: boolean;
  isResettingPaneWidth: boolean;
  leftWidth: number;
  rightWidth: number;
  zoomLevel: number;
}

export const WorkspacePaneLayoutRoot = styled('section', {
  shouldForwardProp: prop => ![
    'centerMinWidth',
    'hasRightPane',
    'isResettingPaneWidth',
    'leftWidth',
    'rightWidth',
    'zoomLevel',
  ].includes(String(prop)),
})<WorkspacePaneLayoutRootStyleProps>(({
  centerMinWidth,
  hasRightPane,
  isResettingPaneWidth,
  leftWidth,
  rightWidth,
  theme,
  zoomLevel,
}) => {
  const resizeHandleWidth = 'var(--workspace-pane-layout-resize-handle-width)';
  const centerColumn = `minmax(${getZoomAdjustedPx(centerMinWidth, zoomLevel)}, 1fr)`;
  const gridTemplateColumns = hasRightPane
    ? [
      getZoomAdjustedPx(leftWidth, zoomLevel),
      resizeHandleWidth,
      centerColumn,
      resizeHandleWidth,
      getZoomAdjustedPx(rightWidth, zoomLevel),
    ].join(' ')
    : [
      getZoomAdjustedPx(leftWidth, zoomLevel),
      resizeHandleWidth,
      centerColumn,
    ].join(' ');

  return {
    '--workspace-pane-layout-focus-outline-width': getZoomAdjustedPx(2, zoomLevel),
    '--workspace-pane-layout-radius-s': getZoomAdjustedPx(theme.shape.radius.s, zoomLevel),
    '--workspace-pane-layout-resize-handle-offset-y': getZoomAdjustedPx(2, zoomLevel),
    '--workspace-pane-layout-resize-handle-margin-y': getZoomAdjustedPx(8, zoomLevel),
    '--workspace-pane-layout-resize-handle-width': getZoomAdjustedPx(
      WORKSPACE_PANE_LAYOUT_DEFAULTS.resizeHandleWidth,
      zoomLevel
    ),
    backgroundColor: theme.palette.neutral[100],
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateColumns,
    height: '100%',
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
    transition: isResettingPaneWidth
      ? theme.transitions.create('grid-template-columns', {
        duration: WORKSPACE_PANE_LAYOUT_DEFAULTS.resizeResetAnimationMs,
        easing: theme.transitions.easing.easeOut,
      })
      : undefined,
    width: '100%',
    [`@media (max-width: ${PRISM_BREAKPOINTS.tablet1024}px)`]: {
      borderRadius: theme.shape.radius.s,
      display: 'grid',
      gridTemplateColumns: '1fr',
      overflow: 'auto',
    },
  };
});

export const WorkspacePaneLayoutPane = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 'var(--workspace-pane-layout-radius-s)',
  boxSizing: 'border-box',
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
}));

export const WorkspacePaneLayoutCenterPane = styled(WorkspacePaneLayoutPane)({});

export const WorkspacePaneLayoutResizeHandle = styled('button')(({ theme }) => ({
  appearance: 'none',
  backgroundColor: theme.palette.background.paper,
  border: 0,
  borderRadius: 'var(--workspace-pane-layout-radius-s)',
  cursor: 'col-resize',
  margin: 'var(--workspace-pane-layout-resize-handle-margin-y) 0',
  minHeight: 0,
  padding: 0,
  position: 'relative',
  transition: theme.transitions.create(['background-color', 'box-shadow'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: 'var(--workspace-pane-layout-resize-handle-width)',
  '&::before': {
    bottom: 0,
    content: '""',
    left: '50%',
    position: 'absolute',
    top: 0,
    transform: 'translateX(-50%)',
    width: 1,
  },
  '&::after': {
    backgroundColor: theme.palette.neutral[300],
    bottom: 'var(--workspace-pane-layout-resize-handle-offset-y)',
    content: '""',
    left: '50%',
    position: 'absolute',
    top: 'var(--workspace-pane-layout-resize-handle-offset-y)',
    transform: 'translateX(-50%)',
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shortest,
    }),
    width: 1,
  },
  '&:hover::after': {
    backgroundColor: theme.palette.primary.main,
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 var(--workspace-pane-layout-focus-outline-width) ${theme.palette.primary.main}`,
    outline: 0,
    zIndex: 1,
  },
  '&:focus-visible::after': {
    backgroundColor: theme.palette.primary.main,
  },
  [`@media (max-width: ${PRISM_BREAKPOINTS.tablet1024}px)`]: {
    display: 'none',
  },
}));
